import { useGetAdvisorInvestorByIdQuery } from '@/store/usersApi';
import { Drawer, Snackbar, Stack } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAdvisorClientAccess } from '@/hooks/useAdvisorClientAccess';
import { resolveClientDetailTab } from '@/modules/clientServices/utils/clientDetailTabs';
import FlyoutAccessErrorState from '../common/FlyoutAccessErrorState';
import FlyoutErrorState from '../common/FlyoutErrorState';
import FlyoutLoadingState from '../common/FlyoutLoadingState';
import { FlyoutErrorBoundary } from '../components';
import { FLYOUT_WIDTH } from '../constants';
import { ClientDetailsFlyoutProps } from '../types';
import ClientContent from './ClientContent';
import ClientHeader from './ClientHeader';

const ClientDetailsFlyout: React.FC<ClientDetailsFlyoutProps> = ({
  open,
  toggleDrawer,
  client,
  initialTabKey,
}) => {
  const { t } = useLocalization();
  const [toastOpen, setToastOpen] = useState(false);
  const [deletedName, setDeletedName] = useState<string>('');
  const handleClose = useCallback(() => {
    toggleDrawer(false);
  }, [toggleDrawer]);

  const access = useAdvisorClientAccess(client?.InvestorId);
  const queryArg = client ? client.InvestorId : skipToken;
  const {
    currentData: data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAdvisorInvestorByIdQuery(queryArg);

  if (!client) return null;

  if (access.isForbidden || access.isNotFound) {
    return (
      <FlyoutAccessErrorState
        open={open}
        onClose={handleClose}
        variant={access.isForbidden ? 'forbidden' : 'notFound'}
        title={
          access.isForbidden
            ? t('clientAccessForbiddenTitle', 'Access denied')
            : t('clientAccessNotFoundTitle', 'Client not found')
        }
        subtitle={t('clientDetails', 'Client Details')}
        showBackAction={access.isNotFound}
      />
    );
  }

  if (access.isLoading || isLoading || (isFetching && !data)) {
    return (
      <FlyoutLoadingState
        open={open}
        onClose={handleClose}
        message={t('clientDetailsFlyout.loadingDetails')}
        showProgress={false}
        dataFieldsCount={6}
        tabsCount={6}
      />
    );
  }

  if (isError || !data) {
    return (
      <FlyoutErrorState
        open={open}
        onClose={handleClose}
        error={error}
        message={t('clientDetailsFlyout.loadFailed')}
        title={t('clientDetailsFlyout.loadErrorTitle')}
        subtitle={t('clientDetailsFlyout.subtitle')}
      />
    );
  }

  return (
    <FlyoutErrorBoundary>
      <Drawer
        anchor='right'
        open={open}
        onClose={handleClose}
        variant='temporary'
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', md: FLYOUT_WIDTH },
            boxSizing: 'border-box',
          },
          paddingTop:
            'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          paddingBottom:
            'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
        }}
      >
        <Stack>
          <ClientHeader
            isFetching={isFetching}
            client={data}
            action={{ kind: 'close', onClick: handleClose }}
          />
          <ClientContent
            client={data}
            tabSelection={{
              mode: 'uncontrolled',
              initialValue: initialTabKey
                ? resolveClientDetailTab(initialTabKey)
                : undefined,
            }}
            onDeleted={(c) => {
              setDeletedName(c.Name);
              setToastOpen(true);
              handleClose();
            }}
          />
        </Stack>
      </Drawer>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => {
          setToastOpen(false);
        }}
        message={t('clientDeletedToast', {
          defaultValue: '{{name}} deleted',
          name: deletedName,
        })}
      />
    </FlyoutErrorBoundary>
  );
};

export default ClientDetailsFlyout;
